import cn from 'classnames'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import Markdown from 'markdown-to-jsx'
import React, {
  ChangeEvent,
  KeyboardEvent,
  ReactElement,
  useState,
} from 'react'
import { useDeactivatePhysicalCard } from 'src/api/mutation/creditcards'
import ListModalCorner from 'src/assets/images/ListModalCorner.png'
import { useTranslate } from 'src/localization/Trns'

import style from './DisconnectPhysicalCardModal.style'

type DisconnectPhysicalCardModalProps = BaseModalProps & {
  creditCardId: string
  onDisconnectComplete?: () => void
}

export default function DisconnectPhysicalCardModal(
  props: DisconnectPhysicalCardModalProps,
): ReactElement {
  const {
    className = '',
    variant = 'popup',
    isOpen,
    onClose,
    onDisconnectComplete,
    creditCardId,
  } = props
  const [value, setValue] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const translate = useTranslate()
  const { mutateAsync: disconnectPhysicalCard } = useDeactivatePhysicalCard()

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const changeValue = e.currentTarget.value
    setValue(changeValue)
  }

  const handleDisconnectClick = async () => {
    setLoading(true)
    await disconnectPhysicalCard(creditCardId)
    setLoading(false)
    setValue('')
    onDisconnectComplete?.()
  }

  const disconnectStringsMatch = value === translate('DISCONNECT')
  const handleClose = (): void => {
    setValue('')
    onClose()
  }

  return (
    <BaseModal
      className={cn('DisconnectPhysicalCardModal', className)}
      variant={variant}
      isOpen={isOpen}
      onClose={handleClose}
      css={style}
      aria-label={translate('Disconnect Physical Card Modal')}
    >
      <div className="flex-column">
        <img
          className="corner-img"
          src={ListModalCorner}
          alt="List Illustration"
        />
        <div className="flex-column gap-10">
          <div className="flex-column gap-5">
            <p className="font-h2">
              {translate('Disconnect Physical Card Transactions')}
            </p>
            <p className="font-regular">
              {translate(
                'This will disconnect your card transaction feed into Extend and you will no longer see your physical card transactions.  All historical transaction data will remain in Extend.  If you enable the feed at a later date, any transactions while disconnected will not be available.',
              )}
            </p>
          </div>
          <div className="flex-column gap-5">
            <Markdown
              className="has-disconnect"
              options={{ disableParsingRawHTML: true }}
            >
              {translate('Confirm by typing **"DISCONNECT"** below:')}
            </Markdown>
            <TextInput
              placeholder={translate('Please type "DISCONNECT"')}
              value={value}
              onChange={handleChange}
            />
          </div>
          <div className="flex-row gap-6 justify-start align-center">
            <Button
              variant="severe"
              onClick={handleDisconnectClick}
              disabled={loading || !disconnectStringsMatch}
              loading={loading}
            >
              {translate('Confirm')}
            </Button>
            <div
              className="cancel-button"
              onClick={handleClose}
              role="button"
              tabIndex={-1}
              onKeyUp={(e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter') {
                  handleClose()
                }
              }}
            >
              <p className="font-linktext">{translate('Cancel')}</p>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
